import { motion, type Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FileClock,
  KeyRound,
  Lock,
  type LucideIcon,
  Plug,
  ScrollText,
  ShieldCheck,
} from 'lucide-react';

import {
  CountUp,
  Eyebrow,
  fadeInUp,
  GradientMesh,
  Reveal,
  sectionViewport,
  usePrefersReducedMotion,
} from '@/components/landing/v2';

/**
 * TrustBar — section 2 of 13. Immediate 安心感 before the first scroll.
 *
 * Rebuilds the consumer-feeling LogoMarquee into a static, enterprise-grade
 * row of real, defensible security / compliance trust signals (per JP-B2B
 * principles: static reads enterprise, marquee reads consumer). Six bordered
 * glass badge pills back the actual platform proofs (AES-256-GCM per-workspace
 * envelope vault, SAML 2.0 / OIDC SSO, TOTP MFA, append-only audit log, 番号法
 * 7-year automated disposal, public REST API v1), then a thin muted ribbon
 * with the engineering-scale numbers in `.num`.
 *
 * Motion: one-shot fadeInUp stagger (0.06) of the pills with a gentle y:-2
 * hover; ribbon numbers via useCountUp. No marquee, no autoplay, no loops.
 * Eager (lazy:false) — above the fold.
 */

interface Pill {
  key: string;
  Icon: LucideIcon;
  /** Hardcoded statutory / domain label (existing convention, JP font). */
  label: string;
  /** Default JP copy so the section reads correctly before locale keys land. */
  fallback: string;
}

interface Stat {
  key: string;
  to: number;
  unit: string;
  fallbackUnit: string;
}

// staggerChildren 0.06 (spec) — a local override of the shared 0.08 container.
const pillStagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

export function TrustBar() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();

  const pills: Pill[] = [
    {
      key: 'vault',
      Icon: Lock,
      label: 'AES-256-GCM',
      fallback: 'AES-256-GCM 暗号化ボルト',
    },
    {
      key: 'sso',
      Icon: KeyRound,
      label: 'SAML 2.0 / OIDC',
      fallback: 'SAML 2.0 / OIDC SSO',
    },
    {
      key: 'mfa',
      Icon: ShieldCheck,
      label: 'TOTP',
      fallback: 'TOTP 多要素認証',
    },
    {
      key: 'audit',
      Icon: ScrollText,
      label: 'append-only',
      fallback: '追記専用 監査ログ',
    },
    {
      key: 'disposal',
      Icon: FileClock,
      label: '番号法',
      fallback: '番号法 7年自動破棄',
    },
    {
      key: 'api',
      Icon: Plug,
      label: 'REST API v1',
      fallback: '公開 REST API v1',
    },
  ];

  const stats: Stat[] = [
    { key: 'modules', to: 36, unit: 'モジュール', fallbackUnit: 'モジュール' },
    { key: 'migrations', to: 26, unit: 'マイグレーション', fallbackUnit: 'マイグレーション' },
    { key: 'routes', to: 49, unit: 'ルート', fallbackUnit: 'ルート' },
    { key: 'locales', to: 14, unit: 'ロケール', fallbackUnit: 'ロケール' },
  ];

  return (
    <section
      aria-labelledby="trust-bar-heading"
      className="relative isolate overflow-hidden border-y border-border/60 bg-muted/30 py-16 md:py-20"
    >
      <GradientMesh blobs={false} />

      <div className="container relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header block — lead line + sub. */}
        <div className="mx-auto max-w-2xl text-center">
          <Reveal>
            <Eyebrow>{t('trustBar.eyebrow', { defaultValue: 'Enterprise Security' })}</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              id="trust-bar-heading"
              className="display serif-jp mt-3 text-2xl font-semibold tracking-tight text-balance md:text-4xl"
            >
              {t('trustBar.lead', {
                defaultValue: 'エンタープライズ水準のセキュリティを、初日から。',
              })}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-3 max-w-xl text-pretty text-sm leading-relaxed text-muted-foreground md:text-base md:leading-loose">
              {t('trustBar.sub', {
                defaultValue:
                  'ワークスペース単位のエンベロープ暗号化。すべてのPIIアクセスを追記専用ログに記録します。',
              })}
            </p>
          </Reveal>
        </div>

        {/* Badge pills — static enterprise row, one-shot stagger reveal. */}
        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={sectionViewport}
          variants={pillStagger}
          className="mx-auto mt-10 flex max-w-3xl flex-wrap items-center justify-center gap-2.5 md:mt-12 md:gap-3"
        >
          {pills.map(({ key, Icon, label, fallback }) => (
            <motion.li
              key={key}
              variants={fadeInUp}
              whileHover={reduced ? undefined : { y: -2 }}
              transition={{ type: 'spring', stiffness: 120, damping: 25 }}
              className="group inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3.5 py-2 shadow-soft backdrop-blur transition-shadow duration-200 hover:border-accent-300 hover:shadow-card-lift dark:hover:border-accent-700"
            >
              <span
                aria-hidden
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-50 text-accent-600 transition-colors group-hover:bg-accent-100 dark:bg-accent-500/15 dark:text-accent-300"
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
              <span className="flex flex-col items-start leading-tight">
                <span className="font-jp text-xs font-medium text-foreground/90 md:text-sm">
                  {t(`trustBar.pills.${key}`, { defaultValue: fallback })}
                </span>
                <span className="num text-[9px] uppercase tracking-wider text-muted-foreground/70">
                  {label}
                </span>
              </span>
            </motion.li>
          ))}
        </motion.ul>

        {/* Engineering-scale ribbon — count-up numbers in .num. */}
        <Reveal delay={0.15}>
          <ul
            aria-label={t('trustBar.ribbonLabel', { defaultValue: 'Engineering scale' })}
            className="mx-auto mt-10 flex max-w-3xl flex-wrap items-center justify-center gap-x-2 gap-y-2 text-muted-foreground md:mt-12"
          >
            {stats.map((s, i) => (
              <li key={s.key} className="inline-flex items-center gap-2">
                <span className="inline-flex items-baseline gap-1.5">
                  <CountUp
                    to={s.to}
                    as="span"
                    className="text-base font-semibold text-foreground md:text-lg"
                  />
                  <span className="font-jp text-xs md:text-sm">
                    {t(`trustBar.ribbon.${s.key}`, { defaultValue: s.unit })}
                  </span>
                </span>
                {i < stats.length - 1 && (
                  <span aria-hidden className="select-none px-1 text-border">
                    ·
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
